import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
  useMemo,
} from "react";
import logBreadcrumbs from "src/utils/logBreadcrumbs";
import { useRegisterOnConsent } from "src/utils/hooks/useRegisterOnConsent";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { createAdEventListener } from "src/components/DisplayAd/createAdEventListener";
import { useFeature } from "../../feature/useFeature";
import useSearch from "../../utils/hooks/useSearch";
import { getGptRenderedDimensions } from "./utils";
import { type SlotConfigId, slotConfigMap } from "./config";
import { getTargeting } from "./targeting";
import { createSlot } from "./createSlot";
import { useScreenTargeting } from "./useScreenTargeting";
export const GOOGLE_ADS_CONSENT_PURPOSES_REQUIRED = [1, 8, 9, 10];
export const GOOGLE_ADS_LEGITIMATE_INTEREST_PURPOSES_REQUIRED: number[] = [];
export const GOOGLE_ADS_SALE_CONSENT_REQUIRED = true;
export const GOOGLE_ADS_SHARING_CONSENT_REQUIRED = true;

// Helpful docs for the GPT library used in this file:
// https://developers.google.com/doubleclick-gpt/reference

// Make sure that googletag.cmd exists as it may not have loaded yet.
// When the GPT JavaScript is loaded, it looks through the cmd array and executes all the functions in order.
window.googletag = window.googletag || { cmd: [] };

// Ensure media.net's queue exists
window.mnjs = window.mnjs || {};
window.mnjs.que = window.mnjs.que || [];

type Props = {
  slotConfigId: SlotConfigId;
  onFilled?: (event: googletag.events.SlotRenderEndedEvent) => void;
  onNotFilled?: () => void;
  onAdViewed?: () => void;
  className?: string;
  setAdViewed?: Dispatch<SetStateAction<boolean>>;
  refreshCount?: number;
  feedPositionId?: number;
  useDynamicDivId?: boolean;
  targetingData?: ReturnType<typeof getTargeting>;
};

/**
 * TODO: Eventually, DisplayAd can do the job of TieredDisplayAd too.
 * If we add an `onFillFailed` and a `tier` prop to DisplayAd, we can update the tier prop every time the ad fails to fill.
 * This will allow us to reuse all the logic here rather than duplicating it in TieredDisplayAd.
 */

export function DisplayAd({
  slotConfigId,
  onFilled,
  onNotFilled,
  onAdViewed,
  className,
  refreshCount,
  feedPositionId,
  targetingData,
  useDynamicDivId = false,
}: Props) {
  const [divId, setDivId] = useState<string | undefined>();
  const [isReady, setIsReady] = useState(false);
  const { searchResponse } = useSearch();
  const adFillStrategy = useFeature("FillAds");
  const delayLoadingPrebid = useFeature("DelayPrebidUntilConsentReady");
  const screenTargeting = useScreenTargeting();

  // We purposefully only take targeting info from the first search response
  // so we don't need to refresh the ads if the user performs a new search.
  const savedTargeting = useMemo(() => {
    if (!searchResponse) return undefined;

    return getTargeting({
      searchResponse,
      adFillStrategy,
      tier: undefined,
      screenTargeting,
      refreshCount,
      feedPositionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResponse]);

  const targeting = targetingData ?? savedTargeting;

  const hasDisplayAdConsent = useRegisterOnConsent(
    GOOGLE_ADS_CONSENT_PURPOSES_REQUIRED,
    GOOGLE_ADS_LEGITIMATE_INTEREST_PURPOSES_REQUIRED,
    GOOGLE_ADS_SALE_CONSENT_REQUIRED,
    GOOGLE_ADS_SHARING_CONSENT_REQUIRED,
  );

  // Queue up loading of the ad.
  useEffect(() => {
    // We can't request an ad if googletag has been removed by an ad blocker.
    if (!window.googletag?.cmd) {
      sendAnalyticsNonInteractionEvent({
        category: "Ads",
        action: "Error",
        label: "initGpt: googletag.cmd missing",
      });
      return;
    }

    // We can't request an ad until we have a search response because
    // we need to know the experiment config to determine experiment targeting
    // for the MobileWebRewrite2020Holdback experiment.
    if (!targeting) {
      return;
    }

    // For the purposes of header bidding, we need to ensure the user has given consent
    // before defining the ad slot.
    if (!hasDisplayAdConsent) {
      return;
    }

    let slot: googletag.Slot;

    window.googletag?.cmd.push(function () {
      let slotDivId = slotConfigMap[slotConfigId].divId;

      slot = createSlot(
        slotConfigMap[slotConfigId],
        targeting,
        useDynamicDivId ? undefined : slotDivId,
      );

      if (useDynamicDivId) {
        // If useDynamicDivId is true we need to use the
        // div ID generated by GPT in createSlot.
        slotDivId = slot.getSlotElementId();
      }

      sendAnalyticsNonInteractionEvent({
        category: "Ads",
        action: "Requesting",
        label: `${slotConfigId}${feedPositionId ? `+${feedPositionId}` : ""}`,
      });

      setDivId(slotDivId);
    });

    return () => {
      // Cleanup the ad when the component unmounts
      if (slot) {
        window.googletag?.destroySlots([slot]);
      }
    };
  }, [
    hasDisplayAdConsent,
    useDynamicDivId,
    slotConfigId,
    targeting,
    feedPositionId,
  ]);

  // Setup event listeners for the ad slot.
  useEffect(() => {
    if (!divId) return;

    const unsubscribeSlotRequested = createAdEventListener(
      "slotRequested",
      divId,
      () => {
        // convert object into Record<String, String>
        const dimensions = Object.entries(targeting ?? {}).reduce<
          Record<string, string>
        >((acc, [key, value]) => {
          // conversion is necessary because not all values are strings
          acc[key] = String(value);
          return acc;
        }, {});

        sendAnalyticsNonInteractionEvent({
          category: "Ads",
          action: "Requested",
          label: `${slotConfigId}${feedPositionId ? `+${feedPositionId}` : ""}`,
          extraInfo: { dimensions },
        });
      },
    );

    const unsubscribeSlotRendered = createAdEventListener(
      "slotRenderEnded",
      divId,
      (event) => {
        // When we fail to get an ad
        if (event.isEmpty) {
          sendAnalyticsNonInteractionEvent({
            category: "Ads",
            action: "FailedToFill",
            label: `${slotConfigId}${feedPositionId ? `+${feedPositionId}` : ""}`,
          });
          onNotFilled?.();
        } else {
          onFilled?.(event);
          logBreadcrumbs("Ads", `Ad rendered: ${slotConfigId}`);
          const dimensions = getGptRenderedDimensions(event);
          sendAnalyticsNonInteractionEvent({
            category: "Ads",
            action: "Gpt:Render",
            label: `${slotConfigId}${feedPositionId ? `+${feedPositionId}` : ""}`,
            extraInfo: { dimensions },
          });
        }
      },
    );

    const unsubscribeImpressionViewable = createAdEventListener(
      "impressionViewable",
      divId,
      () => {
        onAdViewed?.();
        sendAnalyticsNonInteractionEvent({
          category: "Ads",
          action: "gpt:AdViewed",
          label: `${slotConfigId}${feedPositionId ? `+${feedPositionId}` : ""}`,
        });
      },
      { once: true },
    );

    setIsReady(true);
    return () => {
      // Cleanup event listeners
      unsubscribeSlotRequested?.();
      unsubscribeSlotRendered?.();
      unsubscribeImpressionViewable?.();
    };
  }, [
    divId,
    slotConfigId,
    targeting,
    onNotFilled,
    onFilled,
    onAdViewed,
    feedPositionId,
  ]);

  // Display the ad once the divId is set and event listeners all registered.
  useEffect(() => {
    if (!divId || !isReady) {
      return;
    }

    window.googletag?.cmd.push(function () {
      // Ensure that the Google bid requests are triggered only after the Media.net Prebid script executes.
      if (delayLoadingPrebid) {
        window.mnjs.que.push(() => {
          window.googletag.display(divId);
        });
      } else {
        window.googletag.display(divId);
      }
    });
  }, [delayLoadingPrebid, divId, isReady]);

  return <div id={divId} className={className} data-testid="displayAd" />;
}
